import { normalize } from 'normalizr';
import {
  IMPORT_SERVICES,
  ADD_SERVICE,
  UPDATE_SERVICE,
  UPDATE_SERVICE_ORDER,
  IMPORT_SINGLE_SERVICE,
  DELETE_SERVICE,
} from './constants';
import HttpV2 from '../../httpv2';
import Http from '../../http';
import * as schema from './schema';
import { startLoadingList, stopLoadingList } from '../../common/hoc/withLoaingList';
import { downloadCsv } from '../reports/helpers';
import { fullName } from '../../common/helpers/users';

const importServices = services => ({
  type: IMPORT_SERVICES,
  response: normalize(services, schema.arrayOfServices),
});

const importSingleService = service => ({
  type: IMPORT_SINGLE_SERVICE,
  response: normalize(service, schema.service),
});

const updateServiceOrderAction = services => ({
  type: UPDATE_SERVICE_ORDER,
  services,
});

const getOrderChangedServices = servicesData =>
  servicesData.reduce((memo, { isOrderChanged, ...serviceData }) => {
    if (isOrderChanged) {
      memo.push(serviceData);
    }

    return memo;
  }, []);

function updateServiceOrder(endpoint, servicesData = []) {
  return (dispatch) => {
    dispatch(updateServiceOrderAction(servicesData));
    return HttpV2.put(endpoint, getOrderChangedServices(servicesData))
      .then((services) => {
        dispatch(importServices(services));
        return services;
      });
  };
}

export function updateBarberUserServiceOrder(shopId, barberId, servicesData) {
  const endpoint = `/shop/${shopId}/barber/${barberId}/service/order?include=shops`;
  return updateServiceOrder(endpoint, servicesData);
}

export function updateShopUserServiceOrder(shopId, servicesData) {
  const endpoint = `/shop/${shopId}/service/order?include=shops`;
  return updateServiceOrder(endpoint, servicesData);
}

export function updateBrandUserServiceOrder(brandId, servicesData) {
  const endpoint = `/brand/${brandId}/service/order`;
  return updateServiceOrder(endpoint, servicesData);
}

function getServices(endpoint, searchText = '', HttpVx = HttpV2) {
  const filter = {
    sortBy: 'order,createdAt:asc',
    limit: 500,
    include: 'shops,category',
  };

  if (searchText) {
    filter.or = true;
    filter.name__like = searchText;
  }

  return (dispatch) => {
    dispatch(startLoadingList());
    return HttpVx.get(endpoint, filter)
      .then((services) => {
        dispatch(importServices(services));
        dispatch(stopLoadingList());
        return services;
      })
      .catch((err) => {
        dispatch(stopLoadingList());
        return err;
      });
  };
}

export function getShopUserServices(shopId, searchText = '') {
  const endpoint = `/shop/${shopId}/service`;
  return getServices(endpoint, searchText, Http);
}

export function getBarberUserServices(shopId, barberId) {
  const endpoint = `/shop/${shopId}/barber/${barberId}/service`;
  return getServices(endpoint, '', Http);
}

export function getBrandUserServices(brandId) {
  const endpoint = `/brand/${brandId}/service`;
  return getServices(endpoint);
}

export function getShopUserServicesV2(shopId, searchText = '') {
  const endpoint = `/shop/${shopId}/service`;
  return getServices(endpoint, searchText);
}

export function getBarberUserServicesV2(shopId, barberId) {
  const endpoint = `/shop/${shopId}/barber/${barberId}/service`;
  return getServices(endpoint);
}

const fetchSingleService = endpoint => dispatch => HttpV2.get(endpoint)
    .then((service) => {
      dispatch(importSingleService(service));
      return service;
    });

export const fetchBrandUserService = (ids) => {
  const endpoint = `/brand/${ids.brandId}/service/${ids.serviceId}`;
  return fetchSingleService(endpoint);
};

export const fetchShopUserService = (ids) => {
  const endpoint = `/shop/${ids.shopId}/service/${ids.serviceId}`;
  return fetchSingleService(endpoint);
};

export const fetchBarberUserService = (ids) => {
  const endpoint = `/shop/${ids.shopId}/barber/${ids.barberId}/service/${ids.serviceId}`;
  return fetchSingleService(endpoint);
};

function exportServicesCsv(endpoint, name) {
  const filter = {
    sortBy: 'order,createdAt:asc',
    include: 'category',
    view: 'csv',
  };

  return () => (
    HttpV2
      .getRaw(endpoint, filter, false)
      .then((response) => {
        downloadCsv(response, `Services - ${name}`);
      })
  );
}

export function exportShopServicesCsv(shop) {
  const endpoint = `/shop/${shop.id}/service`;
  return exportServicesCsv(endpoint, shop.name);
}

export function exportBarberServicesCsv(barber) {
  const endpoint = `/shop/${barber.shopId}/barber/${barber.id}/service`;
  return exportServicesCsv(endpoint, fullName(barber));
}

export function exportBrandServicesCsv(brand) {
  const endpoint = `/brand/${brand.id}/service`;
  return exportServicesCsv(endpoint, brand.name);
}

export function addBrandUserService(brandId, data = {}) {
  return dispatch => HttpV2.post(`/brand/${brandId}/service`, data)
    .then((service) => {
      dispatch({
        type: ADD_SERVICE,
        response: normalize(service, schema.service),
        data,
      });
      return service;
    });
}

export function addBarberUserService(shopId, barberId, data = {}) {
  return dispatch => HttpV2.post(`/shop/${shopId}/barber/${barberId}/service`, data)
    .then((service) => {
      dispatch({
        type: ADD_SERVICE,
        response: normalize(service, schema.service),
        data,
      });
      return service;
    });
}

export function addService(shopId, data = {}) {
  return dispatch => HttpV2.post(`/shop/${shopId}/service`, data)
    .then((service) => {
      dispatch({
        type: ADD_SERVICE,
        response: normalize(service, schema.service),
        data,
      });
      return service;
    });
}

const updateServiceCommon = (url, data) => dispatch => HttpV2.put(url, data)
  .then((service) => {
    dispatch({
      type: UPDATE_SERVICE,
      response: normalize(service, schema.service),
      data,
    });

    return service;
  });

export function updateBarberUserService(shopId, barberId, serviceId, data) {
  const url = `/shop/${shopId}/barber/${barberId}/service/${serviceId}`;
  return updateServiceCommon(url, data);
}

export function updateService(shopId, serviceId, data = {}) {
  const url = `/shop/${shopId}/service/${serviceId}`;
  return updateServiceCommon(url, data);
}

export function updateBrandUserService(brandId, serviceId, data) {
  const url = `/brand/${brandId}/service/${serviceId}`;
  return updateServiceCommon(url, data);
}

export function deleteService(shopId, serviceId) {
  return dispatch => HttpV2.delete(`/shop/${shopId}/service/${serviceId}`)
    .then(() => {
      dispatch({ type: DELETE_SERVICE, serviceId });
      return serviceId;
    });
}

export function deleteBarberService(shopId, barberId, serviceId) {
  return dispatch => HttpV2.delete(`/shop/${shopId}/barber/${barberId}/service/${serviceId}`)
    .then(() => {
      dispatch({ type: DELETE_SERVICE, serviceId });
      return serviceId;
    });
}

export function deleteBrandService(brandId, serviceId) {
  return dispatch => HttpV2.delete(`/brand/${brandId}/service/${serviceId}`)
    .then(() => {
      dispatch({ type: DELETE_SERVICE, serviceId });
      return serviceId;
    });
}

/**
 * Assigns service to shop barbers
 * @param shopId
 * @param serviceId
 * @param barbers - array of barbers ID
 * @returns object[] - array of shop assignments
 */
export function assignShopServiceToBarbers(shopId, serviceId, barbers) {
  return HttpV2.post(`/shop/${shopId}/service/${serviceId}/assign`, { barbers });
}

/**
 * Saves shop assignment on the brand level
 * @param brandId
 * @param serviceId
 * @param id - shop id
 * @param assigneeId - existent shop id
 * @param assignment - assignment data
 * @returns object - assignment object
 */
export function saveBrandShopAssignment(brandId, serviceId, { id, assigneeId, ...assignment }) {
  return HttpV2.post(`/brand/${brandId}/service/${serviceId}/save-assignment`, {
    assignmentType: 'shop',
    assigneeId: assigneeId || id,
    ...assignment,
  });
}

/**
 * Saves shop assignment on the shop level
 * @param shopId
 * @param serviceId
 * @param id - shop id
 * @param assigneeId - existent shop id
 * @param assignment - assignment data
 * @returns object - assignment object
 */
export function saveShopOwnAssignment(shopId, serviceId, { id, assigneeId, ...assignment }) {
  return HttpV2.post(`/shop/${shopId}/service/${serviceId}/save-assignment`, {
    assignmentType: 'shop',
    assigneeId: assigneeId || id,
    ...assignment,
  });
}

/**
 * Saves barber assignment on both brand and shop levels
 * @param shopId
 * @param serviceId
 * @param id - barber id
 * @param assignment - assignment data
 * @returns object - assignment object
 */
export function saveShopBarberAssignment(shopId, serviceId, { id, ...assignment }) {
  return HttpV2.post(`/shop/${shopId}/service/${serviceId}/save-assignment`, {
    assignmentType: 'barber',
    assigneeId: id,
    ...assignment,
  });
}

export function unAssignShopAssignment(shopId, serviceId) {
  return HttpV2.delete(`/shop/${shopId}/service/${serviceId}`);
}

export function resetAllBarbersServiceOrder(shopId) {
  return () => HttpV2.put(`/shop/${shopId}/barber/service/order/reset`);
}

export function resetBarberServicesOrder(shopId, barberId) {
  return () => HttpV2.put(`/shop/${shopId}/barber/${barberId}/service/order/reset`);
}
